import { useState } from "react";
import { useTranslation } from "react-i18next";

type FormState = "idle" | "submitting" | "success" | "error";

// 문의는 사이트의 서버 함수(functions/api/contact.ts)가 회사 ERP 메일서버(SMTP)로 직접 발송해
// info@oceanconnect.co.kr 에 전달합니다. 로컬 개발 서버(vite)에는 이 함수가 없어 전송이 실패하면 메일 앱으로 대체합니다.
const FORM_ENDPOINT = `${import.meta.env.BASE_URL}api/contact`;
const CONTACT_EMAIL = "info@oceanconnect.co.kr";

// Google Analytics 전환 기록 (index.html 의 gtag 가 없으면 무시)
const trackLead = (category: string) => {
  const w = window as Window & { gtag?: (...args: unknown[]) => void };
  w.gtag?.("event", "generate_lead", { event_category: "contact_form", event_label: category });
};

const buildMailto = (f: Record<string, string>) => {
  const subject = `[홈페이지 문의] ${f.category || ""} - ${f.name || ""}`;
  const body = [
    `이름: ${f.name || ""}`,
    `회사/소속: ${f.company || ""}`,
    `이메일: ${f.email || ""}`,
    `연락처: ${f.phone || ""}`,
    `문의 유형: ${f.category || ""}`,
    "",
    f.message || "",
  ].join("\n");
  return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

export default function ContactSection() {
  const { t } = useTranslation();
  const [state, setState] = useState<FormState>("idle");
  const [formError, setFormError] = useState<string>("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const hp = String(formData.get("website_alt") || "").trim();
    if (hp) {
      setState("success");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    // Message length check
    const msg = String(formData.get("message") || "");
    if (msg.length > 500) {
      setState("error");
      setFormError(t("contact.err.length"));
      return;
    }

    const fields = Object.fromEntries(
      [...formData.entries()].map(([k, v]) => [k, String(v)])
    ) as Record<string, string>;
    // 문의 유형은 선택값(예: maintenance) 대신 화면 문구(예: 선박 IT 통합 유지보수)로 보냄
    if (fields.category) fields.category = t(`contact.category.${fields.category}`);

    setState("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(fields),
      });

      // 서버 함수가 없는 환경(로컬 개발 서버 등)이면 메일 앱으로 대체
      if (response.status === 404 || response.status === 405) {
        window.location.href = buildMailto(fields);
        setState("success");
        form.reset();
        return;
      }

      const parsed = (await response.json().catch(() => null)) as { success?: boolean } | null;
      if (response.ok && parsed?.success) {
        trackLead(fields.category || "");
        setState("success");
        form.reset();
      } else {
        setState("error");
        setFormError(t("contact.err.fail"));
      }
    } catch {
      setState("error");
      setFormError(t("contact.err.network"));
    }
  };

  const infoItems: { icon: string; titleKey: string; valueKey: string; href?: string; hintKey?: string; hintAccent?: boolean }[] = [
    { icon: "ri-map-pin-2-line", titleKey: "contact.info1.title", valueKey: "contact.info1.value" },
    { icon: "ri-phone-line", titleKey: "contact.info2.title", valueKey: "contact.info2.value", href: "tel:+82517150611" },
    { icon: "ri-customer-service-2-line", titleKey: "contact.info5.title", valueKey: "contact.info5.value", href: "tel:+827075880611" },
    { icon: "ri-mail-line", titleKey: "contact.info3.title", valueKey: "contact.info3.value", href: "mailto:info@oceanconnect.co.kr" },
    { icon: "ri-kakao-talk-line", titleKey: "contact.kakao.title", valueKey: "contact.kakao.value", hintKey: "contact.kakao.hint" },
    { icon: "ri-time-line", titleKey: "contact.info4.title", valueKey: "contact.info4.value", hintKey: "contact.info4.hint", hintAccent: true },
  ];

  return (
    <section id="contact" className="bg-background-50 py-24 md:py-32 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 md:px-10 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left info */}
          <div className="lg:col-span-5" data-reveal>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold tracking-wider mb-4">
              <i className="ri-mail-send-line"></i>
              CONTACT
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight mb-5">
              {t("contact.title1")}
              <br />
              <span className="text-primary-600">{t("contact.title2")}</span>
            </h2>
            <p className="text-foreground-600 text-base leading-relaxed mb-8">
              {t("contact.desc")}
            </p>

            <div className="space-y-4">
              {infoItems.map((it) => (
                <div key={it.titleKey} className="flex gap-4 items-start">
                  <div className="w-11 h-11 flex items-center justify-center rounded-md bg-background-50 border border-background-200 text-primary-600 shrink-0">
                    <i className={`${it.icon} text-lg`}></i>
                  </div>
                  <div>
                    <div className="text-xs text-foreground-500 uppercase tracking-wider mb-1 font-label">
                      {t(it.titleKey)}
                    </div>
                    {it.href ? (
                      <a
                        href={it.href}
                        className="text-foreground-900 font-medium text-sm md:text-base hover:text-primary-600 cursor-pointer"
                      >
                        {t(it.valueKey)}
                      </a>
                    ) : (
                      <div className="text-foreground-900 font-medium text-sm md:text-base">
                        {t(it.valueKey)}
                      </div>
                    )}
                    {it.hintKey && (
                      <div className={`text-xs mt-0.5 ${it.hintAccent ? "text-primary-600 font-semibold" : "text-foreground-500"}`}>
                        {t(it.hintKey)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7" data-reveal style={{ "--reveal-delay": "150ms" } as React.CSSProperties}>
            <div className="bg-background-50 rounded-lg border border-background-200 p-6 md:p-8">
              {state === "success" ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-accent-500 text-background-50 mx-auto mb-5">
                    <i className="ri-check-line text-3xl"></i>
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-foreground-950 mb-3">
                    {t("contact.success.title")}
                  </h3>
                  <p className="text-foreground-600 mb-6">
                    {t("contact.success.desc")}
                  </p>
                  <button
                    onClick={() => setState("idle")}
                    className="px-6 py-3 rounded-md bg-foreground-950 text-background-50 font-semibold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {t("contact.new")}
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  id="oceanconnect-contact-form"
                  className="space-y-4"
                >
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {t("contact.form.note")}
                  </p>

                  {/* Honeypot */}
                  <input
                    className="hp-field"
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-foreground-700 mb-2 uppercase tracking-wider">
                        {t("contact.form.name")} <span className="text-primary-500">*</span>
                      </label>
                      <input
                        required
                        name="name"
                        type="text"
                        placeholder={t("contact.placeholder.name")}
                        className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground-700 mb-2 uppercase tracking-wider">
                        {t("contact.form.company")}
                      </label>
                      <input
                        name="company"
                        type="text"
                        placeholder={t("contact.placeholder.company")}
                        className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-foreground-700 mb-2 uppercase tracking-wider">
                        {t("contact.form.email")} <span className="text-primary-500">*</span>
                      </label>
                      <input
                        required
                        name="email"
                        type="email"
                        placeholder="you@example.com"
                        className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground-700 mb-2 uppercase tracking-wider">
                        {t("contact.form.phone")}
                      </label>
                      <input
                        name="phone"
                        type="tel"
                        placeholder={t("contact.placeholder.phone")}
                        className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-2 uppercase tracking-wider">
                      {t("contact.form.category")} <span className="text-primary-500">*</span>
                    </label>
                    <select
                      required
                      name="category"
                      defaultValue=""
                      className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                    >
                      <option value="" disabled>
                        {t("contact.category.default")}
                      </option>
                      <option value="maintenance">{t("contact.category.maintenance")}</option>
                      <option value="network">{t("contact.category.network")}</option>
                      <option value="satellite">{t("contact.category.satellite")}</option>
                      <option value="security">{t("contact.category.security")}</option>
                      <option value="newbuild">{t("contact.category.newbuild")}</option>
                      <option value="parts">{t("contact.category.parts")}</option>
                      <option value="etc">{t("contact.category.etc")}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-2 uppercase tracking-wider">
                      {t("contact.form.message")} <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      required
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder={t("contact.placeholder.message")}
                      className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 resize-none"
                    ></textarea>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      required
                      type="checkbox"
                      name="agree"
                      value="Y"
                      className="mt-1 w-4 h-4 accent-primary-500 cursor-pointer"
                    />
                    <span className="text-xs text-foreground-600 leading-relaxed">
                      {t("contact.agree")}
                    </span>
                  </label>

                  {state === "error" && formError && (
                    <div className="p-3 rounded-md bg-primary-50 border border-primary-200 text-primary-800 text-sm">
                      <i className="ri-error-warning-line mr-1"></i>
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={state === "submitting"}
                    className="w-full py-4 rounded-md bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    {state === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        {t("contact.sending")}
                      </>
                    ) : (
                      <>
                        {t("contact.submit")}
                        <i className="ri-send-plane-2-line"></i>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
